/**
 * Token Scopes Selector Component
 *
 * Checkbox group for selecting token permission scopes.
 * Displays scopes grouped by category with descriptions.
 */
import React, { useState, useEffect } from 'react';
import {
  Checkbox,
  Stack,
  Text,
  Paper,
  Group,
  Badge,
  Accordion,
  Alert,
  Loader,
  Box
} from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import tokens from '../../../theme/designTokens';
import { fetchAvailableScopes } from '../../../services/api/apiTokensService';

/**
 * TokenScopesSelector Component
 *
 * @param {Array<string>} selectedScopes - Currently selected scopes
 * @param {Function} onChange - Callback when scopes change
 * @param {boolean} disabled - Disable all checkboxes
 */
const TokenScopesSelector = ({ selectedScopes = [], onChange, disabled = false }) => {
  const [scopesData, setScopesData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch available scopes from API
  useEffect(() => {
    const loadScopes = async () => {
      try {
        setLoading(true);
        const data = await fetchAvailableScopes();
        setScopesData(data);
        setError(null);
      } catch (err) {
        console.error('Failed to load scopes:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadScopes();
  }, []);

  // Handle scope toggle
  const handleScopeToggle = (scope) => {
    if (disabled) return;

    const newScopes = selectedScopes.includes(scope)
      ? selectedScopes.filter(s => s !== scope)
      : [...selectedScopes, scope];

    onChange(newScopes);
  };

  // Handle group toggle (select/deselect all in group)
  const handleGroupToggle = (groupScopes) => {
    if (disabled) return;

    const allSelected = groupScopes.every(scope => selectedScopes.includes(scope));

    const newScopes = allSelected
      ? selectedScopes.filter(s => !groupScopes.includes(s))
      : [...new Set([...selectedScopes, ...groupScopes])];

    onChange(newScopes);
  };

  // Loading state
  if (loading) {
    return (
      <Box style={{ textAlign: 'center', padding: tokens.spacing.xl }}>
        <Loader size="md" color={tokens.colors.brand.primary} />
        <Text size="sm" c={tokens.colors.neutral[6]} mt="md">
          Загрузка доступных прав...
        </Text>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert
        icon={<IconInfoCircle size={16} />}
        title="Ошибка загрузки"
        color="red"
        variant="light"
      >
        {error}
      </Alert>
    );
  }

  // No data
  if (!scopesData || !scopesData.groups) {
    return (
      <Alert
        icon={<IconInfoCircle size={16} />}
        title="Нет данных"
        color="orange"
        variant="light"
      >
        Не удалось загрузить список доступных прав
      </Alert>
    );
  }

  // Build scopes map for quick lookup
  const scopesMap = {};
  scopesData.scopes.forEach(scope => {
    scopesMap[scope.scope] = scope;
  });

  return (
    <Stack gap="md">
      {/* Selected count badge */}
      {selectedScopes.length > 0 && (
        <Group gap="xs">
          <Badge
            color="portal-blue"
            variant="light"
            size="lg"
          >
            Выбрано прав: {selectedScopes.length}
          </Badge>
        </Group>
      )}

      {/* Scopes grouped by category */}
      <Accordion
        variant="separated"
        radius="md"
        defaultValue={scopesData.groups[0]?.group_name}
        styles={{
          item: {
            backgroundColor: tokens.colors.neutral[1],
            border: `1px solid ${tokens.colors.neutral[3]}`
          }
        }}
      >
        {scopesData.groups.map((group) => {
          const groupScopes = group.scopes;
          const selectedCount = groupScopes.filter(s => selectedScopes.includes(s)).length;
          const allSelected = groupScopes.length > 0 && selectedCount === groupScopes.length;

          return (
            <Accordion.Item key={group.group_name} value={group.group_name}>
              <Accordion.Control>
                <Group justify="space-between">
                  <Text fw={600} size="sm" c={tokens.colors.neutral[8]}>
                    {group.group_name}
                  </Text>
                  <Badge
                    color={selectedCount > 0 ? 'portal-green' : 'gray'}
                    variant="light"
                    size="sm"
                  >
                    {selectedCount}/{groupScopes.length}
                  </Badge>
                </Group>
              </Accordion.Control>

              <Accordion.Panel>
                <Stack gap="sm">
                  {/* Select/Deselect all in group */}
                  {groupScopes.length > 1 && (
                    <Paper
                      p="xs"
                      style={{
                        backgroundColor: tokens.colors.neutral[0],
                        border: `1px solid ${tokens.colors.neutral[3]}`
                      }}
                    >
                      <Checkbox
                        label={
                          <Text size="sm" fw={500} c={tokens.colors.brand.primary}>
                            {allSelected ? 'Снять все' : 'Выбрать все'}
                          </Text>
                        }
                        checked={allSelected}
                        indeterminate={selectedCount > 0 && !allSelected}
                        onChange={() => handleGroupToggle(groupScopes)}
                        disabled={disabled}
                      />
                    </Paper>
                  )}

                  {/* Individual scope checkboxes */}
                  {groupScopes.map((scopeKey) => {
                    const scopeInfo = scopesMap[scopeKey];
                    if (!scopeInfo) return null;

                    return (
                      <Paper
                        key={scopeKey}
                        p="sm"
                        style={{
                          backgroundColor: selectedScopes.includes(scopeKey)
                            ? tokens.colors.brand.primary + '08'
                            : 'transparent',
                          border: `1px solid ${
                            selectedScopes.includes(scopeKey)
                              ? tokens.colors.brand.primary
                              : tokens.colors.neutral[3]
                          }`,
                          cursor: disabled ? 'not-allowed' : 'pointer'
                        }}
                        onClick={() => !disabled && handleScopeToggle(scopeKey)}
                      >
                        <Checkbox
                          label={
                            <Stack gap={4}>
                              <Group gap="xs">
                                <Text size="sm" fw={500} c={tokens.colors.neutral[8]}>
                                  {scopeInfo.name}
                                </Text>
                                <Badge size="xs" color="gray" variant="outline">
                                  {scopeInfo.methods.join(', ')}
                                </Badge>
                              </Group>
                              <Text size="xs" c={tokens.colors.neutral[6]}>
                                {scopeInfo.description}
                              </Text>
                              <Text size="xs" c={tokens.colors.neutral[5]} fs="italic">
                                {scopeInfo.endpoint_pattern}
                              </Text>
                            </Stack>
                          }
                          checked={selectedScopes.includes(scopeKey)}
                          onChange={() => handleScopeToggle(scopeKey)}
                          disabled={disabled}
                          styles={{
                            input: {
                              cursor: disabled ? 'not-allowed' : 'pointer'
                            }
                          }}
                        />
                      </Paper>
                    );
                  })}
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>
          );
        })}
      </Accordion>

      {/* Warning if no scopes selected */}
      {selectedScopes.length === 0 && (
        <Alert
          icon={<IconInfoCircle size={16} />}
          title="Внимание"
          color="orange"
          variant="light"
        >
          Необходимо выбрать хотя бы одно право доступа
        </Alert>
      )}
    </Stack>
  );
};

export default TokenScopesSelector;
